/**
 * AccountRegistrationPage Component
 */

import tr from '../../../../locales';
import * as notificationActions from '../../../../redux/actions/notifications';
import * as uiActions from '../../../../redux/actions/ui';
import * as userActions from '../../../../redux/actions/user';
import { ProfileFragment } from '../../../../relay/profile';
import './styles.scss';

import { RouteMatch } from 'found';
import { reduce, snakeCase } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import * as Velocity from 'velocity-animate';
import { VelocityTransitionGroup } from 'velocity-react';

import Page from '../../../../containers/Page';
import {
  IPardotSendNewUserPayload, ISendPardotNewsletterSignupPayload,
  sendNewsletterSignup, sendNewUser
} from '../../../../pardot/actions';
// import Button from '../../../atoms/buttons/Button';
import { IconCheck } from '../../../atoms/images/Icon';
import Heading from '../../../atoms/text/Heading';
import { Span } from '../../../atoms/text/Label';
import LoadingButton from '../../../molecules/buttons/LoadingButton';
import CheckboxWithLabel from '../../../molecules/forms/CheckboxWithLabel';
import { DiscoverPagePath } from '../../browse/Discover';
import { industries, jobTypes } from './config';
import DotNavigation from './DotNavigation';
import { ConnectedRegistrationStepCompanyName } from './RegistrationStepCompanyName';
import RegistrationStepIndustry from './RegistrationStepIndustry';
import RegistrationStepJobType from './RegistrationStepJobType';
import RegistrationStepPhone from './RegistrationStepPhone';
import RegistrationStepUserName from './RegistrationStepUserName';

export const RegistrationPagePath = '/registration';

interface IOwnStateProps {
  profile: GQL.IProfile;
  registrationForm: IUserRegistrationForm | undefined;
  subscribesToNewsletter: boolean;
}

interface IOwnHiddenDispatchProps {
  requestUpdateUserProfile: (payload: IRequestUpdateUserProfileActionPayload) => void;
}

interface IOwnDispatchProps {
  initHeader: () => void;
  pushNotification: (notification: INotificationProps) => void;
  resetHeader: () => void;
  setHeaderActions: (actions: JSX.Element[]) => void;
  setUserSubscribesToNewsletter: (subscribes: boolean) => void;
  sendNewUserToPardot: (payload: IPardotSendNewUserPayload) => void;
  sendNewsletterSignupToPardot: (payload: ISendPardotNewsletterSignupPayload) => void;
}

interface IOwnMergedProps {
  requestUpdateUserProfile: () => Promise<any>;
  toggleUserSubscribesToNewsletter: () => void;
}

interface IOwnProps extends RouteMatch {
  viewer: GQL.IViewer;
}

type AccountRegistrationPageProps = IOwnProps & IOwnStateProps & IOwnDispatchProps & IOwnMergedProps;
interface IOwnState {
  currentStep: StepCount;
  processCompleted: boolean;
  submitting: boolean;
}

const classes = new BEM('account-registration');
const defaultList = [];

const registrationSteps = [
  RegistrationStepUserName,
  RegistrationStepJobType,
  ConnectedRegistrationStepCompanyName,
  RegistrationStepIndustry,
  RegistrationStepPhone,
];

type StepCount = 0 | 1 | 2 | 3 | 4 | 5;

class AccountRegistrationPage extends React.Component<AccountRegistrationPageProps, IOwnState> {
  private askForUserName: boolean = true;

  constructor(props) {
    super(props);

    if (!this.askForUserName) {
      registrationSteps.shift();
    }

    this.state = {
      currentStep: 0,
      processCompleted: false,
      submitting: false,
    };
  }

  componentDidMount() {
    this.props.initHeader();
    this.props.setUserSubscribesToNewsletter(true);

    setTimeout(() => {
      this.setState({ currentStep: 1 });
    }, 1500);
  }

  componentDidUpdate() {
    const el = findDOMNode(this) as HTMLElement;
    const stepIndicator = el.getElementsByClassName('account-registration__step-indicator');
    if (stepIndicator.length) {
      Velocity.hook(stepIndicator[0], 'translateX', '-50%');
    }
  }

  componentWillUnmount() {
    this.props.resetHeader();
  }

  onStepComplete(step: StepCount) {
    if (step < registrationSteps.length) {
      const nextStep = step + 1 as StepCount;
      this.setState({ currentStep: nextStep });

      // Remove the "skip" button for the time being to force users to complete
      // the registration steps.
      // if (registrationSteps[nextStep - 1] === RegistrationStepJobType) {
      //   this.props.setHeaderActions([(
      //     <Button
      //       onClick={this.onClickDone}
      //       label="Skip"
      //       size="small-medium"
      //     />
      //   )]);
      // }
    } else {
      this.props.setHeaderActions([]);
      this.setState({ processCompleted: true });
    }
  }

  onClickDone = async () => {
    const {
      location: {
        query,
      },
      router,
    } = this.props;

    this.setState({ submitting: true });

    await this.updateProfile();

    const redirectUrl = query && query.goto ? query.goto : DiscoverPagePath;
    router.push(redirectUrl);
  }

  renderStep() {
    const RegistrationStep = registrationSteps[this.state.currentStep - 1];
    return (
      <RegistrationStep
        key={`step-${this.state.currentStep}`}
        onComplete={() => this.onStepComplete(this.state.currentStep)}
      />
    );
  }

  render() {
    const {
      currentStep,
      processCompleted,
    } = this.state;
    return (
      <VelocityTransitionGroup
        component={Page}
        id="account-registration"
        layout="strict"
        {...classes() }
        enter={{ animation: 'transition.expandIn', duration: 500, delay: 350, display: 'flex' }}
        leave={{ animation: 'transition.shrinkOut', duration: 350 }}
        runOnMount
      >
        {currentStep === 0 &&
          <div {...classes('step') }>
            <div className="signup-check"><IconCheck /></div>
            <Heading is="h2"  {...classes('step__title') }>Thanks for signing up!</Heading>
          </div>
        }
        {(currentStep > 0 && !processCompleted) && this.renderStep()}
        {(currentStep > 0 && !processCompleted) &&
          <DotNavigation
            currentDot={currentStep}
            totalDots={registrationSteps.length}
            {...classes('step-indicator') }
          />
        }
        {processCompleted &&
          <div {...classes('step', 'process-complete') }>
            <Heading is="h2"  {...classes('step__title') }>
              Thank you for taking the time.<br />We really appreciate it.
            </Heading>
            <Span size="large">
              Sign up to the Music Discovery Club to receive trending music in your inbox every two weeks.
            </Span>
            <CheckboxWithLabel
              onChange={() => this.props.toggleUserSubscribesToNewsletter()}
              checked={this.props.subscribesToNewsletter}
              label="I would like to join the Music Discovery Club."
              name="newsletter-opt-in"
            />
            <LoadingButton
              onClick={this.onClickDone}
              isLoading={this.state.submitting}
              label="Complete Registration"
              colorScheme="dark"
              size="medium"
            />
          </div>
        }
      </VelocityTransitionGroup>
    );
  }

  private async updateProfile() {
    const {
      pushNotification,
      requestUpdateUserProfile,
    } = this.props;

    try {
      await requestUpdateUserProfile();
    } catch (error) {
      pushNotification({
        autoDismiss: false,
        message: tr.errors.genericMessage,
        state: 'error',
      });
      console.error(error);
    }

    return true;
  }
}

const ConnectedAccountRegistrationPage = connect(
  ({ user: { profile, registrationForm } }: IStoreState): IOwnStateProps => ({
    profile: profile!,
    registrationForm,
    subscribesToNewsletter: !!(registrationForm && registrationForm.optInToNewsletter),
  }),
  (dispatch): IOwnDispatchProps & IOwnHiddenDispatchProps => ({
    initHeader: () => {
      dispatch(uiActions.setHeaderType({ type: 'simple' }));
    },
    pushNotification(notification) {
      dispatch(notificationActions.pushNotification(notification));
    },
    resetHeader: () => {
      dispatch(uiActions.setHeaderType({ type: 'extended' }));
      dispatch(uiActions.setHeaderActions({ actions: defaultList }));
    },
    requestUpdateUserProfile: (payload) => {
      dispatch(userActions.requestUpdateUserProfile(payload));
    },
    setHeaderActions: (actions) => {
      dispatch(uiActions.setHeaderActions({ actions }));
    },
    setUserSubscribesToNewsletter: (value) => {
      dispatch(userActions.setUserDetail({ key: 'optInToNewsletter', value }));
    },
    sendNewUserToPardot: (payload) => dispatch(sendNewUser(payload)),
    sendNewsletterSignupToPardot: (payload) => dispatch(sendNewsletterSignup(payload)),
  }),
  (stateProps, dispatchProps, ownProps: IOwnProps): AccountRegistrationPageProps => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    toggleUserSubscribesToNewsletter: () => {
      dispatchProps.setUserSubscribesToNewsletter(!stateProps.subscribesToNewsletter);
    },
    requestUpdateUserProfile: () => {
      const form = stateProps.registrationForm!;
      const input: GQL.IUpdateProfileInput = reduce((form as any), (update, value, key) => {
        update[snakeCase(key)] = value;
        return update;
      }, { is_new_user: false });

      dispatchProps.sendNewUserToPardot({
        email: stateProps.profile.email!,
        first_name: input.first_name!,
        last_name: input.last_name!,
        phone: input.phone!,
        company_name: input.company_name!,
        company_type: (industries.find((val) => val.id === input.industry_id) || { label: undefined }).label!,
        role: (jobTypes.find((val) => val.id === input.job_type_id) || { label: undefined }).label!,
      });

      // if (input.opt_in_to_newsletter) {
      //   input.opt_in_to_newsletter = false;
      //   dispatchProps.sendNewsletterSignupToPardot({ email: stateProps.profile.email! });
      // }

      return new Promise((resolve, reject) => {
        dispatchProps.requestUpdateUserProfile({
          input,
          promise: { resolve, reject },
          viewerId: ownProps.viewer.id,
        });
      });
    },
  }),
)(AccountRegistrationPage);

const RelayAccountRegistration = createFragmentContainer(
  ConnectedAccountRegistrationPage,
  ProfileFragment,
);

export default RelayAccountRegistration;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/account/Registration/index.tsx